import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Truck, CheckCircle2, Clock, XCircle, CornerDownRight } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateInTz, formatLocalDateInTz } from '../../utils/timezone';
import type { NurseryOrder, OrderShipments, ShipmentFamilyOrder } from '../../types/nursery';

export interface OrderShipmentsCardProps {
  order: NurseryOrder;
  timezone: string;
}

const n = (x: number) => x.toLocaleString();

/**
 * What went when, across an order and its backorders (Rob, 2026-10-19).
 *
 * A short despatch carries the rest to <order>-B1, whose own short despatch
 * carries it on to <order>-B1-B1, and nobody could tell from any one page
 * which trees had gone on which load. This card reads the whole chain from
 * the original order (GET /nursery/orders/:id/shipments) and shows it the
 * same on every order in it: per tree, what went on which order and load and
 * on what day, and what is still to go; then each order in the chain.
 * Renders nothing on an order with no backorder chain.
 */
export function OrderShipmentsCard({ order, timezone }: OrderShipmentsCardProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'OrderShipmentsCard' });
  const inChain = !!order.parent_order_id || (order.backorders?.length ?? 0) > 0;
  const [data, setData] = useState<OrderShipments | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!inChain) return;
    let live = true;
    setError(null);
    nurseryService.getOrderShipments(order.id)
      .then(res => {
        if (!live) return;
        if (!res.success) throw res;
        setData(res.data);
      })
      .catch(e => { if (live) setError(errorMessage(e, t('loadFailed'))); });
    return () => { live = false; };
    // updated_at: a despatch or allocation elsewhere on the page reloads the order.
  }, [order.id, order.updated_at, inChain, t]);

  if (!inChain) return null;

  const byId = new Map((data?.orders ?? []).map(o => [o.id, o]));
  const orderLink = (id: number) => {
    const o = byId.get(id);
    if (!o) return null;
    return id === order.id
      ? <span className="font-medium text-neutral-800">{o.order_number}</span>
      : <Link to={`/nursery/orders/${id}`} className="font-medium text-primary-700 underline">{o.order_number}</Link>;
  };
  const loadLink = (o: ShipmentFamilyOrder) => o.load_id && o.load_number
    ? <Link to={`/nursery/loads/${o.load_id}`} className="text-primary-700 underline">{o.load_number}</Link>
    : null;
  const shippedDate = (o: ShipmentFamilyOrder) => o.despatched_at ? formatDateInTz(o.despatched_at, timezone) : null;

  const totals = data?.totals;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide flex items-center gap-2">
          <Truck className="h-4 w-4 text-neutral-400" /> {t('title')}
        </h3>
        {data && (
          <p className="text-sm text-neutral-600 break-words">
            {t('intro', { root: data.root_order_number })}
            {data.root_id !== order.id && (
              <>{' '}<Link to={`/nursery/orders/${data.root_id}`} className="text-primary-700 underline">{t('openOriginal')}</Link></>
            )}
          </p>
        )}
      </div>

      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
      {!data && !error && <p className="text-sm text-neutral-400">{t('loading')}</p>}

      {data && totals && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Total label={t('ordered')} value={totals.ordered} tone="neutral" />
            <Total label={t('shipped')} value={totals.shipped} tone="green" />
            <Total label={t('waiting')} value={totals.waiting} tone="amber" />
            <Total label={t('notSent')} value={totals.not_sent} tone="red" />
          </div>

          {/* Per tree: the answer to "what went when" */}
          <div>
            <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">{t('byTree')}</h4>
            <div className="space-y-2">
              {data.items.map(it => (
                <div key={it.key} className="border border-neutral-100 rounded-lg px-4 py-3 space-y-1.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <p className="text-sm font-medium text-neutral-800 break-words min-w-0">
                      {it.item_name || t('unnamed')}
                      {it.tree_type_name && <span className="text-neutral-500 font-normal">{' · '}{it.tree_type_name}</span>}
                    </p>
                    <p className="text-sm text-neutral-600">{t('shippedOf', { shipped: n(it.shipped), ordered: n(it.ordered) })}</p>
                  </div>
                  <ul className="space-y-1 text-sm">
                    {it.shipped_in.map(s => {
                      const o = byId.get(s.order_id);
                      if (!o) return null;
                      const date = shippedDate(o);
                      return (
                        <li key={`s${s.order_id}`} className="flex items-start gap-2 text-neutral-700 break-words">
                          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                          <span className="min-w-0">
                            {date ? t('itemShippedOn', { n: n(s.quantity), date }) : t('itemShipped', { n: n(s.quantity) })}
                            {' · '}{orderLink(s.order_id)}
                            {loadLink(o) && <>{' · '}{loadLink(o)}</>}
                          </span>
                        </li>
                      );
                    })}
                    {it.waiting_in.map(w => {
                      const o = byId.get(w.order_id);
                      if (!o) return null;
                      return (
                        <li key={`w${w.order_id}`} className="flex items-start gap-2 text-neutral-700 break-words">
                          <Clock className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                          <span className="min-w-0">
                            {t('itemWaiting', { n: n(w.quantity) })}{' '}{orderLink(w.order_id)}
                            {' · '}{t(`status.${o.status}`, { defaultValue: o.status.replace(/_/g, ' ') })}
                            {loadLink(o) && <>{' · '}{loadLink(o)}</>}
                          </span>
                        </li>
                      );
                    })}
                    {it.not_sent > 0 && (
                      <li className="flex items-start gap-2 text-neutral-700">
                        <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                        <span className="min-w-0">{t('itemNotSent', { n: n(it.not_sent) })}</span>
                      </li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* The chain itself, original first */}
          <div>
            <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">{t('chain')}</h4>
            <ol className="space-y-2">
              {data.orders.map(o => {
                const date = shippedDate(o);
                const kids = o.backorder_ids.filter(id => byId.has(id));
                const short = o.outcome === 'shipped' || o.outcome === 'nothing_sent' ? o.trees_ordered - o.trees_shipped : 0;
                return (
                  <li key={o.id}
                    className={`border rounded-lg px-4 py-3 space-y-1 ${o.id === order.id ? 'border-primary-300 bg-primary-50/40' : 'border-neutral-100'}`}
                    style={{ marginLeft: `${Math.min(o.depth, 3) * 12}px` }}>
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                      <span className="flex items-center gap-1.5 text-sm min-w-0 break-words">
                        {o.depth > 0 && <CornerDownRight className="h-4 w-4 text-neutral-400 shrink-0" />}
                        {orderLink(o.id)}
                        {o.id === order.id && <span className="text-xs text-primary-700">{t('thisOrder')}</span>}
                      </span>
                      <Outcome o={o} date={date} />
                    </div>
                    {(loadLink(o) || o.carrier_name || o.consignment_ref) && (
                      <p className="text-sm text-neutral-600 break-words">
                        {loadLink(o)}
                        {o.outcome === 'open' && o.load_despatch_date && (
                          <>{' · '}{t('loadFor', { date: formatLocalDateInTz(o.load_despatch_date.slice(0, 10), timezone) })}</>
                        )}
                        {o.carrier_name && <>{' · '}{o.carrier_name}</>}
                        {o.consignment_ref && <>{' · '}{t('consignment', { ref: o.consignment_ref })}</>}
                      </p>
                    )}
                    {o.outcome === 'open' && !o.load_id && <p className="text-sm text-neutral-500">{t('noLoad')}</p>}
                    <ul className="text-sm text-neutral-700 space-y-0.5">
                      {o.lines.map((l, i) => {
                        const qty = o.outcome === 'shipped' ? l.quantity_shipped : l.quantity_ordered;
                        if (o.outcome === 'shipped' && qty === 0) return null;
                        return (
                          <li key={`${l.key}-${i}`} className="break-words">
                            <span className="font-medium tabular-nums">{n(qty)}</span>{' '}{l.item_name || t('unnamed')}
                            {l.tree_type_name && <span className="text-neutral-500">{' · '}{l.tree_type_name}</span>}
                            {o.outcome === 'shipped' && l.quantity_ordered > l.quantity_shipped && (
                              <span className="text-neutral-500">{' '}{t('lineOf', { ordered: n(l.quantity_ordered) })}</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    {short > 0 && kids.length > 0 && (
                      <p className="text-sm text-neutral-600 break-words">
                        {t('carriedTo', { n: n(short) })}{' '}
                        {kids.map((id, i) => <span key={id}>{i > 0 && ', '}{orderLink(id)}</span>)}
                      </p>
                    )}
                    {short > 0 && kids.length === 0 && o.outcome === 'shipped' && (
                      <p className="text-sm text-neutral-600">{t('shortCancelled', { n: n(short) })}</p>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </>
      )}
    </div>
  );
}

function Total({ label, value, tone }: { label: string; value: number; tone: 'neutral' | 'green' | 'amber' | 'red' }) {
  const tones = {
    neutral: 'bg-neutral-50 text-neutral-800',
    green: 'bg-green-50 text-green-700',
    amber: value > 0 ? 'bg-amber-50 text-amber-700' : 'bg-neutral-50 text-neutral-500',
    red: value > 0 ? 'bg-red-50 text-red-700' : 'bg-neutral-50 text-neutral-500',
  };
  return (
    <div className={`rounded-lg p-2.5 text-center ${tones[tone]}`}>
      <p className="text-lg font-bold tabular-nums">{n(value)}</p>
      <p className="text-xs font-medium">{label}</p>
    </div>
  );
}

function Outcome({ o, date }: { o: ShipmentFamilyOrder; date: string | null }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'OrderShipmentsCard' });
  const pill = 'text-xs font-medium rounded-full px-2 py-0.5';
  switch (o.outcome) {
    case 'shipped':
      return (
        <span className={`${pill} bg-green-100 text-green-800`}>
          {date ? t('outcome.shippedOn', { n: n(o.trees_shipped), date }) : t('outcome.shipped', { n: n(o.trees_shipped) })}
        </span>
      );
    case 'nothing_sent':
      return <span className={`${pill} bg-neutral-100 text-neutral-700`}>{t('outcome.nothingSent')}</span>;
    case 'cancelled':
      return <span className={`${pill} bg-red-100 text-red-800`}>{t('outcome.cancelled')}</span>;
    default:
      return (
        <span className={`${pill} bg-amber-100 text-amber-800`}>
          {t('outcome.open', { n: n(o.trees_ordered), status: t(`status.${o.status}`, { defaultValue: o.status.replace(/_/g, ' ') }) })}
        </span>
      );
  }
}
